/* global localStorage */
/* eslint-disable camelcase */
import axios from 'axios'
import { createSelector } from 'reselect'
import amplitude from '../util/amplitudeTrigger'
import API from '../config'
import { call, put, takeLatest, takeEvery, cancelled, select } from 'redux-saga/effects'
import LocalStorageMock from '../util/localStorageMock'
import history from '../history'
import { get } from 'lodash-es'
import { normalize } from 'normalizr'
import { user as userSchema } from '../schemas'
import { getHeaders } from './api'

// replace localStorage to mock when run module from tests
// eslint-disable-next-line
const localStorage = (window && window.localStorage) || new LocalStorageMock()

// ------------------------------------
// Constants
// ------------------------------------
const rootApi = `${API.apiPath}/${API.apiVersion}/`

const getRequestConfig = user => ({
  timeout: API.requestTimeout,
  baseURL: `${API.apiPath}/${API.apiVersion}/`,
  headers: getHeaders(user.data.auth_token)
})

const REQUEST_UPDATE_USER_DATA = 'REQUEST_UPDATE_USER_DATA'
export const UPDATE_USER_DATA = 'UPDATE_USER_DATA'
const SET_USER_FB = 'SET_USER_FB'
const SET_USER = 'SET_USER'
const REQUEST_LOGOUT = 'REQUEST_LOGOUT'
export const LOGOUT = 'LOGOUT'
const SET_PLAYER_ID = 'SET_PLAYER_ID'
const REQUEST_LOGIN = 'REQUEST_LOGIN'
const REPORT_DEVICE = 'REPORT_DEVICE'
const UNLINK_DEVICE = 'UNLINK_DEVICE'
const SET_ACCOUNTS = 'SET_ACCOUNTS'
const REQUEST_UPLOAD_THUMBNAIL = 'REQUEST_UPLOAD_THUMBNAIL'
const SET_TEMPORARY_THUMB = 'SET_TEMPORARY_THUMB'
const SET_ACTIVITIES_STATUS = 'SET_ACTIVITIES_STATUS'

// ------------------------------------
// Actions
/* ------------------------------------ */
export const requestUpdateUserData = dataObj => ({ type: REQUEST_UPDATE_USER_DATA, payload: { data: dataObj } })
export const updateUserData = dataObj => ({ type: UPDATE_USER_DATA, payload: { data: dataObj } })
export const requestUploadThumbnail = file => ({ type: REQUEST_UPLOAD_THUMBNAIL, payload: { file } })
export const setTemporaryThumb = url => ({ type: SET_TEMPORARY_THUMB, payload: url })
export const setActivitiesStatus = status => ({ type: SET_ACTIVITIES_STATUS, payload: status })

function* processUpdateUserData({ payload }) {
  const url = `${rootApi}auth/edit_user`
  const { data } = payload
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  const CancelToken = axios.CancelToken
  const source = CancelToken.source()
  // yield put(updateUserData(normalize({ ...user, ...data }, userSchema)))

  try {
    const response = yield call([axios, 'post'], url, data, {
      headers: getHeaders(token),
      cancelToken: source.token
    })
    yield put(updateUserData(normalize(response.data, userSchema)))
  } catch (e) {
    yield put(updateUserData(normalize(user, userSchema)))
  } finally {
    if (yield cancelled()) {
      source.cancel()
    }
  }
}

function* updateUserDataSaga() {
  yield takeEvery(REQUEST_UPDATE_USER_DATA, processUpdateUserData)
}

export function setUserFB(fb) {
  return { type: SET_USER_FB, payload: fb }
}

export function setUser(data) {
  return { type: SET_USER, payload: data }
}

export function requestLogin(fbAccessToken) {
  return {
    type: REQUEST_LOGIN,
    fbAccessToken
  }
}

function setAccounts(payload) {
  return {
    type: SET_ACCOUNTS,
    payload
  }
}

function* getAccounts(action, xhr = axios) {
  const response = yield call([xhr, 'post'], `${rootApi}auth/fb_signin_v3`, {
    fb_access_token: action.fbAccessToken
  })
  if (get(response, 'data.artificial_accounts.length')) {
    yield put(setAccounts(response.data))
  } else {
    amplitude('setUserId', response.data.user.user.id)
    amplitude('logEvent', 'logIn')
    // window.Appcues.identify(response.data.user.user.id, {
    //   name: response.data.user.user.username,
    //   displayName: response.data.user.user.first_name,
    //   isNew: new Date().getTime() / 1000 - response.data.user.user.confirmed_at < 200
    // })
    yield put(setUser(response.data.user))
  }
}

export function* login(action, xhr = axios) {
  const response = yield call([xhr, 'post'], `${rootApi}auth/fb_signin_v3`, {
    fb_access_token: action.fbAccessToken
  })
  amplitude('setUserId', response.data.user.user.id)
  amplitude('logEvent', 'logIn')
  yield put(setUser(response.data))
}

export const requestLogout = () => ({ type: REQUEST_LOGOUT })

const logout = () => ({ type: LOGOUT })

function* processLogout() {
  yield put(logout())
  setTimeout(() => history.push('/'), 0)
}

export function setPlayerId(playerId) {
  return {
    type: SET_PLAYER_ID,
    playerId
  }
}

export function* getAccountsSaga() {
  yield takeLatest(REQUEST_LOGIN, getAccounts)
}

export function* loginSaga() {
  yield takeLatest(REQUEST_LOGIN, login)
}

export function* logoutSaga() {
  yield takeLatest(REQUEST_LOGOUT, processLogout)
}

export function requestReportDevice(payload) {
  return {
    type: REPORT_DEVICE,
    payload
  }
}

function* reportDevice(action, xhr = axios) {
  const { user, userId } = action.payload
  yield call(
    [xhr, 'post'],
    `${rootApi}auth/report_device`,
    {
      device_type: 'web',
      device_token: userId
    },
    getRequestConfig(user)
  )
  yield put(setPlayerId(userId))
}

function* reportDeviceSaga() {
  yield takeLatest(REPORT_DEVICE, reportDevice)
}

export function requestUnlinkDevice(payload) {
  return {
    type: UNLINK_DEVICE,
    payload
  }
}

function* unlinkDevice(action, xhr = axios) {
  const { user, userId } = action.payload
  yield call(
    [xhr, 'post'],
    `${rootApi}auth/unlink_device`,
    {
      device_type: 'web',
      device_token: userId
    },
    getRequestConfig(user)
  )
}

function* unlinkDeviceSaga() {
  yield takeLatest(UNLINK_DEVICE, unlinkDevice)
}

function* uploadThumbnail(action, xhr = axios) {
  const { file } = action.payload
  const user = yield select(selectUser)
  const uploadParams = yield call(
    [xhr, 'post'],
    `${rootApi}deck/generate_image_upload_params`,
    {
      file_name: file.name.replace(/ /g, '-')
    },
    getRequestConfig(user)
  )
  const bucketParams = uploadParams.data.result
  const formData = new FormData()
  formData.append('AWSAccessKeyId', bucketParams.fields.AWSAccessKeyId)
  formData.append('key', bucketParams.fields.key)
  formData.append('policy', bucketParams.fields.policy)
  formData.append('signature', bucketParams.fields.signature)
  formData.append('file', file)
  yield call([xhr, 'post'], `https://${bucketParams.bucket_name}.s3.amazonaws.com`, formData, {
    headers: { 'x-amz-acl': bucketParams.fields['x-amz-acl'] }
  })
  yield put(setTemporaryThumb(bucketParams.url + bucketParams.fields.key))
}

function* uploadThumbnailSaga() {
  yield takeLatest(REQUEST_UPLOAD_THUMBNAIL, uploadThumbnail)
}

export const sagas = [
  getAccountsSaga,
  logoutSaga,
  reportDeviceSaga,
  unlinkDeviceSaga,
  updateUserDataSaga,
  uploadThumbnailSaga
]

// ------------------------------------
// Reducer
/* ------------------------------------ */
const userData = JSON.parse(localStorage.getItem('userData'))

const userDataInitial = {
  auth_token: '',
  user: {
    active: false,
    background_url: '',
    bio: '',
    confirmed_at: null,
    education: null,
    graduation_year: null,
    id: '',
    username: ''
  }
}

const initialState = {
  data: userData || userDataInitial,
  fb: null,
  error: null,
  accounts: [],
  temporaryThumb: '',
  hasUncheckedActivities: false
}

export const selectUser = getState => getState.user

export const getUserState = userState => userState.user || initialState

export const getUserDataState = createSelector(
  getUserState,
  userState => userState.data
)

export const getLoggedInUser = createSelector(
  getUserDataState,
  userDataState => userDataState.user
)

export const getLoggedInUserId = createSelector(
  getLoggedInUser,
  loggedInUser => loggedInUser.id
)

export default function(state = initialState, action) {
  switch (action.type) {
    case SET_USER_FB:
      return Object.assign({}, state, {
        fb: action.payload
      })
    case SET_USER: {
      const user = action.payload
      localStorage.setItem('userData', JSON.stringify(user))
      return Object.assign({}, state, {
        data: user,
        accounts: []
      })
    }
    case SET_ACCOUNTS: {
      return Object.assign({}, state, {
        accounts: [action.payload.user, ...action.payload.artificial_accounts]
      })
    }
    case SET_TEMPORARY_THUMB: {
      return Object.assign({}, state, {
        temporaryThumb: action.payload
      })
    }
    case SET_ACTIVITIES_STATUS: {
      return Object.assign({}, state, {
        hasUncheckedActivities: action.payload
      })
    }
    case LOGOUT:
      localStorage.removeItem('userData')
      return Object.assign({}, state, {
        data: null
      })
    default:
      return state
  }
}
